import { Component, OnInit, AfterContentInit, Input, NgZone, ViewChild, ContentChild, TemplateRef} from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { get } from 'lodash';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/filter';

import { FileUploader } from 'ng2-file-upload';

import { FormTemplateDef, DefaultFormTemplateDef } from '../templates';
import { FormPlaceholder } from '../internal-templates';

import { Field } from './field';

@Component({
  selector: 'form-generator',
  template : `
            <form [formGroup]="formGroup" (ngSubmit)="submit()" class="formGenerator" novalidate>
                <div class="error" *ngIf="error">
                    <mat-card>
                        {{error}}
                    </mat-card>
                </div>
                <ng-container formPlaceholder></ng-container>
                <ng-container *defaultFormTemplateDef="let formData">
                  <div class="form" fxLayout="{{formData.options.direction}} wrap" [fxLayoutGap]="formData.options.gap" fxLayoutAlign="start">
                    <div class="formElement" *ngFor="let model of formData.blueprint">
                      <form-element [model]="model" [group]="formData.group"></form-element>
                    </div>
                  </div>
                </ng-container>
            </form>
              `,
  styles : [`
            .valid {
              color: green;
              margin-left: 8px;
            }

            .invalid {
              color: red;
              margin-left: 8px;
            }

            .error {
              margin-bottom: 18px;

              .mat-card {
                color: #fff;
                background-color: #F44336;
                padding: 12px;
              }
            }
          `]
})
export class FormGeneratorComponent implements OnInit, AfterContentInit {
  @Input() formBlueprint: any[] = [];
  @Input() set formOptions(value: any) {
      this._formOptions = Object.assign(this._formOptions, value);

      if(this._formOptions.items !== undefined) {
        this._formOptions.items.subscribe((items) => {
          this.zone.run(() => {
            this.items = items;
            this.updateItems(this.formBlueprint);
          });
        })
      }

      if(this._formOptions.values !== undefined) {
        this._formOptions.values.subscribe((values) => {
          this.zone.run(() => {
            this.values = values;
            this.updateValues(this.formBlueprint, this.formGroup);
          });
        });
      }

      if(this._formOptions.parameters !== undefined) {
        this._formOptions.parameters.subscribe((parameters) => {
          this.zone.run(() => {
            this.updateUploaders(parameters);
          });
        });
      }
  };

  @ViewChild(FormPlaceholder) _formPlaceholder: FormPlaceholder;
  @ViewChild(DefaultFormTemplateDef) _defaultFormTemplateDef: DefaultFormTemplateDef;
  @ContentChild(FormTemplateDef) _formTemplate: FormTemplateDef;

  public formGroup: FormGroup;
  public error: string;
  private noUpdate: boolean = false;

  private _formOptions: any = {
    direction: 'column',
    gap: '20px'
  };
  private values: any = null;
  private items: any = null;

  constructor(private zone: NgZone) {

  }

  ngOnInit() {
    this.formGroup = new FormGroup({});

    this.formBlueprint.forEach((field, key) => {
      this.formBlueprint[key] = Object.assign(new Field(), field);

      if (field.type == 'group') {
        this.generateGroup(field, this.formBlueprint[key]);
      } else {
        this.generateField(field, this.formBlueprint[key], this.formGroup);
      }
    });

    this.updateValues(this.formBlueprint, this.formGroup);
  }

  ngAfterContentInit() {
    this._formPlaceholder.viewContainer.clear();
    if(this._formTemplate !== undefined) {
      this._formPlaceholder.viewContainer.createEmbeddedView(this._formTemplate.template, { $implicit: { options: this._formOptions, blueprint: this.formBlueprint, group: this.formGroup }});
    } else {
      this._formPlaceholder.viewContainer.createEmbeddedView(this._defaultFormTemplateDef.template, { $implicit: { options: this._formOptions, blueprint: this.formBlueprint, group: this.formGroup }});
    }
  }

  public submit() {
    this._formOptions.callback(this.formGroup, this.setError);
  }

  public setError = (error) => {
    this.zone.run(() => this.error = error);
  }

  public calcHeight(rows, height) {
    let calcHeight = rows * height;

    return { height: calcHeight + 'px' };
  }

  private updateUploaders(parameters) {
    if(parameters) {
      this.formBlueprint.forEach((field) => {
        if(parameters[field.name]) {
          field.uploader.options.additionalParameter = parameters[field.name];
        }
      });
    }
  }

  private generateGroup(field, data) {
    this.formGroup.addControl(field.name, new FormGroup({}));

    field.items.forEach((value, key) => {
      value[key] = Object.assign(new Field(), value);

      this.generateField(value, value[key], this.formGroup.get(field.name));
    });
  }

  private getValue(field) {
    let value: any;
    if (this.values) {
      if (this.values[field.name]) {
        value = this.values[field.name];
      }
    }

    return value;
  }

  private updateValues(fields, group, name = '') {
    if(group !== undefined) {
      fields.forEach((field) => {
        if(field.type === 'group') {
          this.updateValues(field.items, group, name + field.name + '.');
        } else {
          this.updateValue(field, group, name);
        }
      });
    }
  }

  private updateValue(field, group, name = '') {
    if(this.values) {
      var value = get(this.values, name + field.name);

      if(value !== undefined) {
        var control = group.get(name + field.name);
        this.noUpdate = true;
        control.patchValue(value);
        this.noUpdate = false;
      }
    }
  }

  updateItems(fields, name = '') {
    fields.forEach((field) => {
      if(field.type === 'group') {
        this.updateItems(field.items, name + field.name + '.');
      } else {
        this.updateItem(field, name);
      }
    });
  }

  updateItem(field, name = '') {
    if (this.items) {
      if (this.items[name + field.name]) {
        this.noUpdate = true;
        field.items = this.items[name + field.name];
        this.noUpdate = false;
      }
    }
  }

  private generateField(field, data, group) {
    if (field.type != 'button' && field.type != 'timed-callback' && field.type != 'group') {
      this.updateItem(field);

      group.addControl(field.name,
        new FormControl({ value: this.getValue(field), disabled: data.disabled}, data.validators));

      if(field.type == 'upload') {
        var _field = this.formBlueprint.filter((value) => { return value.name == field.name; })[0];
        _field.uploader = new FileUploader({ url: field.url, isHTML5: true, autoUpload: field.autoUpload, disableMultipart: field.disableMultipart, allowedMimeType: field.allowedMimeType, headers: field.headers });
        _field.uploader.onCompleteItem = (item:any, response:any, status:any, headers:any) => {
          var json = JSON.parse(response);
          group.get(field.name).setValue(json.body);
        }
      }
    }

    if (field.type === 'timed-callback') {
      this.formGroup.valueChanges
      .filter(
        // Filters the update events when we set the values by script
        () => { return !this.noUpdate; },
      )
      .debounceTime(field.time).subscribe(() => {
        this.submit();
      });
    }
  }
}
